/**
 * PhotoSwipe v5 mobile zoom for product galleries.
 *
 * Activates ONLY on coarse-pointer / no-hover devices (phones, tablets).
 * On desktop, the original Dawn lightbox behavior is preserved.
 *
 * Reads its image list from a <script type="application/json"
 * id="ProductPhotoSwipeData-<sectionId>"> emitted by
 * snippets/product-photoswipe-data.liquid.
 *
 * Replaces the click handler on every .product__media-toggle inside
 * .product__modal-opener--image so that taps open PhotoSwipe (with real
 * pinch-zoom + double-tap zoom + swipe-to-dismiss) instead of the flat
 * Dawn modal.
 */
(function () {
  'use strict';

  // Version tag — bump when changes ship so the user can confirm in
  // DevTools console that the latest JS is actually deployed.
  var VERSION = 'photoswipe-init v8 (2026-04-29 instant msrc placeholder)';
  console.log('[product-photoswipe] loaded:', VERSION);

  var MOBILE_QUERY = '(hover: none) and (pointer: coarse)';

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function parseDataSource(sectionId) {
    var script = document.getElementById('ProductPhotoSwipeData-' + sectionId);
    if (!script) return null;
    try {
      var data = JSON.parse(script.textContent);
      return Array.isArray(data) && data.length ? data : null;
    } catch (err) {
      console.error('[product-photoswipe] Invalid JSON for section ' + sectionId, err);
      return null;
    }
  }

  function bindGallery(gallery) {
    var match = gallery.id.match(/^MediaGallery-(.+?)(?:-duplicate)?$/);
    if (!match) return;
    var sectionId = match[1];

    var dataSource = parseDataSource(sectionId);
    if (!dataSource) return;

    if (!window.PhotoSwipeLightbox || !window.PhotoSwipe) {
      console.warn('[product-photoswipe] PhotoSwipe globals missing, aborting bind');
      return;
    }

    var lightbox = new window.PhotoSwipeLightbox({
      dataSource: dataSource,
      pswpModule: window.PhotoSwipe,
      showHideAnimationType: 'fade',
      bgOpacity: 1,
      wheelToZoom: true,
      zoom: true,
      counter: true,
      arrowKeys: false,
      // Hide caption-less zoom UI on tiny screens (looks cleaner)
      padding: { top: 20, bottom: 20, left: 0, right: 0 }
    });

    // Two-pronged backdrop strategy. Previous single-strategy attempts
    // each failed in the user's environment for different reasons:
    //   - Styling .pswp__bg failed: it's display:none under our config.
    //   - Adding a child div inside .pswp failed: .pswp's stacking
    //     context (contain + transform) seems to swallow children's
    //     visual coverage in this theme.
    //   - A body-level backdrop alone might fail if a page element has
    //     z-index higher than ours.
    // Doing BOTH in parallel:
    //   1) Paint .pswp itself with `background-color: #000` !important
    //      — this is the most direct route since .pswp already covers
    //      100vw/100vh and has z-index 100000.
    //   2) Append a fixed div directly to <body> at z-index 99999.
    // Either alone would suffice; together they're redundant insurance.
    var BACKDROP_COLOR = '#000000';

    function showBackdrop() {
      var existing = document.querySelector('.theme-pswp-body-backdrop');
      if (existing && existing.parentNode) {
        existing.parentNode.removeChild(existing);
      }
      var backdrop = document.createElement('div');
      backdrop.className = 'theme-pswp-body-backdrop';
      // z-index: 2147483646 (max signed 32-bit integer minus 1) is
      // intentionally absurd — it ensures we are above any element on
      // the page (including third-party widgets, Shopify apps, sticky
      // headers with custom z-indexes, etc.) regardless of their value.
      // We leave 2147483647 for PhotoSwipe (also forced to that, below)
      // so the photo and controls render on top of our backdrop.
      backdrop.style.cssText =
        'position:fixed;' +
        'top:0;left:0;right:0;bottom:0;' +
        'inset:0;' +
        'width:100vw;' +
        'height:100vh;' +
        'background:' + BACKDROP_COLOR + ';' +
        'z-index:2147483646;' +
        'pointer-events:none;' +
        'opacity:0;' +
        'transition:opacity 333ms ease;';
      document.body.appendChild(backdrop);

      // Diagnostic: confirm the rendered bbox and surface any element
      // that might compete on z-index. The user can paste these values
      // back if the backdrop still doesn't show visually.
      var rect = backdrop.getBoundingClientRect();
      var cs = getComputedStyle(backdrop);
      console.log('[product-photoswipe] body backdrop appended', {
        bbox: { x: rect.x, y: rect.y, w: rect.width, h: rect.height },
        bg: cs.backgroundColor,
        zIndex: cs.zIndex,
        opacity: cs.opacity,
        display: cs.display,
        visibility: cs.visibility
      });

      requestAnimationFrame(function () {
        backdrop.style.opacity = '1';
      });
    }

    function hideBackdrop() {
      var backdrop = document.querySelector('.theme-pswp-body-backdrop');
      if (!backdrop) return;
      backdrop.style.opacity = '0';
      setTimeout(function () {
        if (backdrop.parentNode) {
          backdrop.parentNode.removeChild(backdrop);
        }
      }, 333);
    }

    // Strategy 1: paint .pswp itself black on every open.
    //
    // We register the same paint logic on *every* PhotoSwipe lifecycle
    // event we know about — different config combinations cause some
    // events not to fire (in the user's environment, `beforeOpen` is
    // confirmed not firing). Whichever fires first wins; the rest are
    // no-ops because the styles will already be set.
    function paintPswp(eventName) {
      return function () {
        var el = (lightbox.pswp && lightbox.pswp.element) ||
                 document.querySelector('.pswp');
        if (!el) return;
        el.style.setProperty('background-color', BACKDROP_COLOR, 'important');
        el.style.setProperty('background-image', 'none', 'important');
        el.style.setProperty('opacity', '1', 'important');
        el.style.setProperty('z-index', '2147483647', 'important');
        console.log('[product-photoswipe] painted .pswp via', eventName);
      };
    }
    [
      'init', 'bindEvents', 'beforeOpen', 'firstUpdate',
      'openingAnimationStart', 'openingAnimationEnd', 'afterInit'
    ].forEach(function (evt) {
      try {
        lightbox.on(evt, paintPswp(evt));
      } catch (err) {
        console.warn('[product-photoswipe] cannot register', evt, err);
      }
    });

    lightbox.on('close', hideBackdrop);
    lightbox.on('destroy', hideBackdrop);

    lightbox.init();

    var buttons = gallery.querySelectorAll('.product__modal-opener--image .product__media-toggle');
    buttons.forEach(function (button) {
      // Clone-and-replace strips Dawn's modal-opener click listener.
      var clone = button.cloneNode(true);
      button.parentNode.replaceChild(clone, button);

      clone.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var mediaId = clone.getAttribute('data-media-id');
        var index = -1;
        for (var i = 0; i < dataSource.length; i++) {
          if (String(dataSource[i].mediaId) === String(mediaId)) {
            index = i;
            break;
          }
        }
        if (index < 0) index = 0;

        // Upgrade msrc with the gallery <img>'s actual currentSrc.
        // The browser has already loaded that exact URL for the gallery
        // thumbnail, so it's served from cache instantly — no network
        // wait before the user sees the photo. The 2048px high-res
        // version then loads in the background and replaces the
        // placeholder once ready.
        var modalOpener = clone.closest('.product__modal-opener');
        var thumbImg = modalOpener && modalOpener.querySelector('.product__media img');
        if (thumbImg) {
          var cachedUrl = thumbImg.currentSrc || thumbImg.src;
          if (cachedUrl) {
            dataSource[index].msrc = cachedUrl;
          }
        }

        showBackdrop();
        lightbox.loadAndOpen(index);

        // Polling fallback: paint .pswp the moment it appears in the
        // DOM. PhotoSwipe v5 events have proven unreliable in this
        // environment (beforeOpen confirmed not firing), so we poll
        // every 16ms (one frame) for up to 1 second after the click.
        // Once painted, we stop. This is a safety net independent of
        // PhotoSwipe's event system.
        var pollTries = 0;
        var pollInterval = setInterval(function () {
          var el = document.querySelector('.pswp');
          if (el) {
            el.style.setProperty('background-color', BACKDROP_COLOR, 'important');
            el.style.setProperty('background-image', 'none', 'important');
            el.style.setProperty('opacity', '1', 'important');
            el.style.setProperty('z-index', '2147483647', 'important');
            console.log('[product-photoswipe] painted .pswp via DOM poll');
            clearInterval(pollInterval);
          } else if (++pollTries > 60) {
            console.warn('[product-photoswipe] .pswp never appeared in DOM');
            clearInterval(pollInterval);
          }
        }, 16);
      });
    });
  }

  function init() {
    if (!isMobile()) return;
    document.querySelectorAll('media-gallery').forEach(bindGallery);
  }

  // Script is defer-loaded, so DOM is parsed by the time we run.
  // Custom element constructors (modal-opener) have already attached their
  // click listeners — cloneNode safely strips them.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
